import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import chat_logo from '../../assets/images/chat_logo.png';
import '../../assets/css/Incident.css';

const Widget = ({ buttonId = 'launch-widget-btn', isInMenu = false }) => {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLanguageModalShowing, setLanguageModalShowing] = useState(false);
  const widgetOpenRef = useRef(false);
  const initializationRef = useRef(false);
  const pendingLaunchCallbackRef = useRef(null);
  const autoOpenStorageKey = 'open_chat_after_language_switch';
  const launchRequestEvent = 'chat-widget-request-launch';

  const getCurrentLanguage = () => {
    return localStorage.getItem('language') === 'fq' ? 'fq' : 'en';
  };

  const getLanguageLabel = (language) => {
    return language === 'fq' ? t('french') : t('english');
  };

  const openWidget = (launchCallback) => {
    if (!widgetOpenRef.current && launchCallback) {
      launchCallback();
      widgetOpenRef.current = true;
      document.body.classList.add('widget-open');
    }
  };

  const closeLanguageModal = () => {
    setLanguageModalShowing(false);
    pendingLaunchCallbackRef.current = null;
  };

  const confirmLanguageAndOpenChat = () => {
    setLanguageModalShowing(false);
    openWidget(pendingLaunchCallbackRef.current);
    pendingLaunchCallbackRef.current = null;
  };

  const switchLanguageAndOpenChat = () => {
    const newLanguage = getCurrentLanguage() === 'en' ? 'fq' : 'en';

    localStorage.setItem('language', newLanguage);
    localStorage.setItem(autoOpenStorageKey, 'true');
    i18n.changeLanguage(newLanguage);

    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  useEffect(() => {
    const handleLaunchRequest = (event) => {
      if (event.detail?.buttonId !== buttonId) {
        return;
      }

      pendingLaunchCallbackRef.current = event.detail.launchCallback;
      setLanguageModalShowing(true);
    };

    window.addEventListener(launchRequestEvent, handleLaunchRequest);

    return () => {
      window.removeEventListener(launchRequestEvent, handleLaunchRequest);
    };
  }, [buttonId]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    if (!initializationRef.current) {
      initializationRef.current = true;
      
      const style = document.createElement('style');
      style.innerHTML = `
        #amazon-connect-open-widget-button {
          display: none !important;
        }
        
        #amazon-connect-close-widget-button {
          position: fixed !important;
          top: 32px !important;
          right: 133px !important;
        }
        
        .launch-widget-btn {
          border-radius: 50% !important;
          cursor: pointer !important;
          transition: opacity 0.3s ease !important;
        }
        
        .launch-widget-btn:not(.in-menu) {
          position: fixed !important;
          top: 32px !important;
          right: 133px !important;
        }
        
        .launch-widget-btn.in-menu {
          position: relative !important;
          top: auto !important;
          right: auto !important;
          margin-right: 10px !important;
          display: inline-block !important;
          vertical-align: middle !important;
        }
        
        .launch-widget-btn.in-menu img {
          width: 45px !important;
          height: 45px !important;
        }
        
        .launch-widget-btn:not(.in-menu) img {
          width: 53px !important;
          height: 53px !important;
        }
        
        body.widget-open .launch-widget-btn {
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
      
      let language = localStorage.getItem('language') || "en";
      const requestOptions = {
        method: 'GET',
        redirect: 'follow'
      };

      const observeWidgetChanges = () => {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'childList') {
              const chatContainer = document.querySelector('[data-amazon-connect-chat-widget]');
              const closeButton = document.getElementById('amazon-connect-close-widget-button');
              
              if (chatContainer) {
                const isOpen = chatContainer.getAttribute('data-state') === 'expanded';
                
                if (isOpen && !widgetOpenRef.current) {
                  widgetOpenRef.current = true;
                  document.body.classList.add('widget-open');
                  
                  if (closeButton) {
                    closeButton.style.display = 'block';
                  }
                } else if (!isOpen && widgetOpenRef.current) {
                  widgetOpenRef.current = false;
                  document.body.classList.remove('widget-open');
                }
              }
              
              if (closeButton) {
                closeButton.style.display = widgetOpenRef.current ? 'block' : 'none';
              }
            }
          }
        });

        observer.observe(document.body, { 
          childList: true, 
          subtree: true 
        });

        return observer;
      };

      (function (w, d, x, id) {
        const s = d.createElement('script');
        s.src = process.env.REACT_APP_CONNECT_WIDGET_SCRIPT_SRC;
        s.async = 1;
        s.id = id;
        d.getElementsByTagName('head')[0].appendChild(s);
        w[x] = w[x] || function () { (w[x].ac = w[x].ac || []).push(arguments) };
      })(window, document, 'amazon_connect', process.env.REACT_APP_CONNECT_WIDGET_ID);

      const urlParams = new URLSearchParams(window.location.search);
      const ticketNumber = urlParams.get('ticketNumber');
      const user_identifier = localStorage.getItem("user_identifier") || "Guest User";

      let participantMessage = `User: ${user_identifier}`;
      if (ticketNumber) {
        participantMessage += ` | Ticket: ${ticketNumber}`;
      }

      window.amazon_connect('language', language === "en" ? "en_US" : "fr_FR");
      window.amazon_connect('snippetId', process.env.REACT_APP_CONNECT_WIDGET_SNIPPET_ID);
      window.amazon_connect('supportedMessagingContentTypes', ['text/plain', 'text/markdown']);
      window.amazon_connect('customerDisplayName', function(callback) {
        callback(user_identifier);
      });

      window.amazon_connect('customizationObject', {
        header: { 
          dropdown: true,
          dynamicHeader: true
        },
        transcript: {
          eventMessages: {
            participantJoined: participantMessage
          }
        },
        endChat: {
          enableConfirmationDialog: true,
          confirmationDialogText: {
              title: "End Chat",
              message: "Are you sure you want to end this chat?",
              confirmButtonText: "End Chat",
              cancelButtonText: "Cancel",
          }
        }
      });

      window.amazon_connect('contactAttributes', {
        user_identifier: user_identifier,
        ...(ticketNumber && { ticketNumber: ticketNumber })
      });

      window.amazon_connect('customLaunchBehavior', {
        skipIconButtonAndAutoLaunch: true,
        programmaticLaunch: (function(launchCallback) {
          const setupClickHandler = (btnId) => {
            const launchWidgetBtn = document.getElementById(btnId);
            if (launchWidgetBtn) {
              if (launchWidgetBtn.dataset.chatLaunchHandlerAttached === btnId) {
                return;
              }

              const existingHandler = launchWidgetBtn.onclick;
              if (existingHandler) {
                launchWidgetBtn.removeEventListener('click', existingHandler);
              }
              
              const clickHandler = function(e) {
                e.preventDefault();
                e.stopPropagation();
                if (!widgetOpenRef.current) {
                  window.dispatchEvent(new CustomEvent(launchRequestEvent, {
                    detail: {
                      buttonId: btnId,
                      launchCallback
                    }
                  }));
                }
              };
              
              launchWidgetBtn.addEventListener('click', clickHandler);
              launchWidgetBtn.dataset.chatLaunchHandlerAttached = btnId;
              
              window.addEventListener('beforeunload', function() {
                launchWidgetBtn.removeEventListener('click', clickHandler);
              });
            }
          };

          setupClickHandler('launch-widget-btn-desktop');
          setupClickHandler('launch-widget-btn-mobile');
          
          setTimeout(() => {
            setupClickHandler('launch-widget-btn-desktop');
            setupClickHandler('launch-widget-btn-mobile');
          }, 500);

          if (localStorage.getItem(autoOpenStorageKey) === 'true') {
            localStorage.removeItem(autoOpenStorageKey);
            setTimeout(() => {
              openWidget(launchCallback);
            }, 500);
          }
        }),
      });

      const setupCloseButtonListener = () => {
        const closeButtonCheckInterval = setInterval(() => {
          const closeButton = document.getElementById('amazon-connect-close-widget-button');
          if (closeButton) {
            clearInterval(closeButtonCheckInterval);
            
            closeButton.addEventListener('click', function() {
              widgetOpenRef.current = false;
              document.body.classList.remove('widget-open');
              
              const chatContainer = document.querySelector('[data-amazon-connect-chat-widget]');
              if (chatContainer) {
                chatContainer.setAttribute('data-state', 'collapsed');
              }
            }, true);
          }
        }, 500);
        
        setTimeout(() => clearInterval(closeButtonCheckInterval), 10000);
      };

      window.amazon_connect('styles', {
        openChat: { color: '#ffffff', backgroundColor: '#C20D00' }, 
        closeChat: { color: '#ffffff', backgroundColor: '#C20D00' },
        buttonText: 'Chat with Us!'
      });

      // Setting French back to FR instead of FQ
      language = language === "en" ? "en" : "fr";

      window.amazon_connect('authenticate', function(callback) {
        callback(
          fetch(`${process.env.REACT_APP_API_GATEWAY_TOKENS_URL}?username=${user_identifier}&language=${language}&version=${process.env.REACT_APP_CONNECT_WIDGET_NAME}`, requestOptions)
            .then(response => response.json())
            .then(result => result.body.TOKEN)
        );
      });

      setTimeout(() => {
        const widgetObserver = observeWidgetChanges();
        setupCloseButtonListener();
      }, 1000);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, buttonId]);

  const isAllowedUser = localStorage.getItem("user_identifier") !== null && localStorage.getItem("user_identifier") !== "";
  const currentLanguage = getCurrentLanguage();
  const alternateLanguage = currentLanguage === 'en' ? 'fq' : 'en';

  return (
    <>
      {isLanguageModalShowing && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h4>{t('ChatLanguage')}</h4>
              <span className="close" onClick={closeLanguageModal}>&times;</span>
            </div>
            <div className="modal-body chat-language-modal-body">
              {t('ChatWillOpenLanguage', { language: getLanguageLabel(currentLanguage) })}
            </div>
            <div className="modal-footer chat-language-modal-footer">
              <button className="incident-buttons chat-language-button" type="button" onClick={confirmLanguageAndOpenChat}>
                {t('Ok')}
              </button>
              <button className="incident-buttons chat-language-button" type="button" onClick={switchLanguageAndOpenChat}>
                {t('SwitchToLanguage', { language: getLanguageLabel(alternateLanguage) })}
              </button>
            </div>
          </div>
        </div>
      )}
      {isAllowedUser && (
        <div className="chat-widget-container">
          <button id={buttonId} className={`launch-widget-btn ${isInMenu ? 'in-menu' : ''}`} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src={chat_logo} alt="Chat" style={{
                width: isMobile ? '35px' : '53px', 
                height: isMobile ? '35px' : '53px' 
            }}/>
          </button>
          {buttonId === 'launch-widget-btn-desktop' && (<div id="amazon-connect-chat-widget"></div>)}
        </div>
      )}
    </>
  );
};

export default Widget;
